import React, { useEffect, useRef, useState } from 'react'

import { Combobox, Loader, TextInput, useCombobox } from '@mantine/core'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { formatAddress, parseReactGoogleAddress } from 'src/lib/address.utils'
import { PLACES_INCLUDED_REGION_CODES } from 'src/lib/placesRegions'

const defaultAddress = {
  street: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
}
/**
 * PlacesAutocomplete component for address search and selection using Google Places API
 *
 * Parameter Details:
 *  - value: This is used to populate the input's text. It expects an address object.
 *    Example: { street: '123 Main St', city: 'Anytown', state: 'CA', zip: '12345' }
 *  - displayStreetOnly: Whether to display only the street part of the address
 *  - handleChange: Callback when address is selected, receives neatly formatted address object
 *    Example: {street: '123 Main St', city: 'Anytown', state: 'CA', zipCode: '12345'}
 *  - handleVerboseChange: Callback when address is selected, receives full Google Places response
 */
const PlacesAutocomplete = ({
  label = null,
  value,
  required = false,
  types = ['street_address', 'subpremise', 'premise', 'route'],
  displayStreetOnly = false,
  handleChange = null,
  handleVerboseChange = null,
  ...props
}) => {
  const [response, setResponse] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const isSelectingPlaceRef = useRef(false)

  //This handles the editable text in the TextInput
  const [address, setAddress] = useState(
    displayStreetOnly ? value?.street || '' : formatAddress(value)
  )

  const fetchPlaces = async (query) => {
    const API_KEY = process.env.GOOGLE_MAPS_API_KEY
    const url = 'https://places.googleapis.com/v1/places:autocomplete'
    const requestBody = {
      input: query,
      includedPrimaryTypes: types,
      includedRegionCodes: [...PLACES_INCLUDED_REGION_CODES],
    }

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': API_KEY,
        },
        body: JSON.stringify(requestBody),
      })

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`)
      }

      const data = await res.json()
      setResponse(data.suggestions || [])
    } catch (error) {
      toast('Error fetching place', 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchPlaceDetails = async (placeId) => {
    const API_KEY = process.env.GOOGLE_MAPS_API_KEY
    const url = `https://places.googleapis.com/v1/places/${placeId}`

    try {
      // Mark that we're selecting a place to prevent useEffect from interfering
      isSelectingPlaceRef.current = true

      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': API_KEY,
          'X-Goog-FieldMask': '*',
        },
      })

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`)
      }

      const data = await res.json()
      if (displayStreetOnly) {
        setAddress(data.displayName.text)
      }
      const placeObject = parseReactGoogleAddress(data)
      if (handleChange) {
        handleChange(placeObject)
      }
      if (handleVerboseChange) {
        handleVerboseChange(data)
      }

      // Reset the flag after a short delay to allow the handleChange to complete
      setTimeout(() => {
        isSelectingPlaceRef.current = false
      }, 100)
    } catch (error) {
      toast('Error fetching place details', 'error')
      isSelectingPlaceRef.current = false
    }
  }

  useEffect(() => {
    // Skip this effect if we're in the middle of selecting a place from the dropdown
    // This prevents the effect from overwriting the full address with just the street
    if (isSelectingPlaceRef.current) {
      return
    }

    if (address.trim()) {
      setIsLoading(true)
      const timeoutId = setTimeout(() => fetchPlaces(address), 300)

      // Update street field while typing (for manual entry support)
      if (handleChange && displayStreetOnly) {
        handleChange({
          ...defaultAddress,
          street: address,
        })
      }

      return () => clearTimeout(timeoutId)
    } else {
      setResponse([])
      if (handleChange) {
        handleChange(defaultAddress)
      }
      if (handleVerboseChange) {
        handleVerboseChange(defaultAddress)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address])

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  })

  return (
    <div className="flex w-full flex-col gap-1">
      {label && (
        <div
          className={`flex flex-row justify-start gap-1 text-sm font-medium text-doubleNickel-gray-700`}
        >
          {label}
          {required && <span className="text-red-500">*</span>}
        </div>
      )}

      <Combobox store={combobox}>
        <Combobox.Target>
          <TextInput
            onFocus={() => combobox.openDropdown()}
            rightSection={isLoading && <Loader size={10} color="gray" />}
            value={address}
            onChange={(e) => {
              setAddress(e.currentTarget.value)
              if (!combobox.dropdownOpened) {
                combobox.openDropdown()
              }
            }}
            {...props}
          />
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>
            {response.map((result) => (
              <Combobox.Option
                key={result.placePrediction.placeId}
                value={result.placePrediction.text.text}
                onClick={() => {
                  const value = result.placePrediction.text.text
                  // Mark that we're selecting a place to prevent useEffect from interfering
                  isSelectingPlaceRef.current = true
                  setAddress(value)
                  const selectedPlace = response.find(
                    (result) => result.placePrediction.text.text === value
                  )
                  if (selectedPlace) {
                    fetchPlaceDetails(selectedPlace.placePrediction.placeId)
                  } else {
                    // Reset if no place found
                    isSelectingPlaceRef.current = false
                  }
                  combobox.closeDropdown()
                }}
              >
                {result.placePrediction.text.text}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </div>
  )
}

export default PlacesAutocomplete
